import * as Sentry from '@sentry/react';

window.Sentry = Sentry;

const isProd = import.meta.env.PROD;
if (isProd) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_LARAVEL_DSN,
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        beforeSend(event) {
            const firstValue = event.exception?.values?.[0];
            const frames = firstValue?.stacktrace?.frames || [];
            const msg = firstValue?.value || '';

            // 3rd party lib errors (not actionable)
            // Note: has3rdPartyFrame only works for externally-loaded scripts (CDN, injected).
            // Bundled deps (react-dom, motion, etc.) get merged into app-*.js,
            // so frame.filename won't contain the lib name — use msg/type checks instead.
            const has3rdPartyFrame = (lib) => frames.some((f) => f.filename?.includes(lib));
            if (has3rdPartyFrame('rocket-loader')) return null;

            // Stripe Elements race conditions (SDK loaded from Stripe CDN)
            if (has3rdPartyFrame('clover/stripe') || msg.includes('could not retrieve data from the specified Element')) return null;

            // Bundled 3rd party errors — filtered by message/type since frame filenames are mangled
            if (msg.includes('Cannot read properties of null') && msg.includes('removeChild')) return null;
            if (msg.includes('toString') && firstValue?.type === 'InertiaError') return null;

            // utils/http.js errors from user connectivity (network drop, request aborted by navigation or our own AbortController)
            // Note: check type not frames — beforeSend runs before sourcemap resolution, so bundled filenames won't help
            if (firstValue?.type === 'HttpError' && msg === 'Network Error') return null;
            if (firstValue?.type === 'CanceledError') return null;
            // Same for Inertia visits, which use Inertia's own XHR client (message is "Network error (<url>)")
            if (firstValue?.type === 'HttpNetworkError' && msg.startsWith('Network error')) return null;

            // Non-Error promise rejections with no value (no stacktrace, not actionable)
            const isEmptyRejection = firstValue?.type === 'UnhandledRejection' && msg.includes('Non-Error promise rejection captured with value:');
            if (isEmptyRejection) return null;

            // Java bridge errors from Instagram/TikTok in-app browsers
            if (msg.includes('Java object is gone')) return null;

            // iOS WKWebView native bridge (not our code)
            if (msg.includes('webkit.messageHandlers')) return null;

            // Android in-app browser JS bridge (Huawei/WeChat-style WebViews), injected script
            if (msg.includes('WebViewJavascriptBridge')) return null;

            // Browser extension interference (DOM manipulation by extensions breaks React's virtual DOM)
            if (msg.includes('insertBefore') || msg.includes('removeChild') || msg.includes('appendChild')) return null;
            if (msg.includes('MetaMask')) return null;

            // Browser API errors in restricted environments (incognito, embedded webviews)
            if (firstValue?.type === 'InvalidAccessError') return null;
            if (firstValue?.type === 'SecurityError') return null;
            if (msg.includes('NotReadableError')) return null;
            if (msg.includes('NotAllowedError: Write permission denied')) return null;

            // Old browser compatibility (not actionable)
            if (msg === 'structuredClone is not defined') return null;
            if (msg === 'Object.hasOwn is not a function') return null;

            // Broken external images
            if (firstValue?.type === 'image.onerror') return null;

            // Stale chunks after deployment (page reloads automatically, not actionable)
            if (msg.includes('Failed to fetch dynamically imported module')) return null;
            if (msg.includes('Importing a module script failed')) return null;

            // Meta (Facebook/Instagram) in-app browser injected scripts
            if (has3rdPartyFrame('iabjs://') || msg.includes('Error invoking postMessage')) return null;
            if (has3rdPartyFrame('promo.v2.js') || msg === 'No extensionAPIBridge') return null;
            if (msg.includes('context-selector-tab-container')) return null;

            // Browser extensions (any frame served from an extension origin)
            if (frames.some((f) => /^(chrome|moz|safari)-extension:\/\//.test(f.filename || ''))) return null;
            if (msg.includes('Converting circular structure to JSON') && msg.includes('__reactFiber$')) return null;

            // WebKit AutoFill / Keychain form scanner
            if (has3rdPartyFrame('FormMetadata.js') || msg.includes('_logicalFormForControl')) return null;

            // Chrome iOS / WKWebView native bridge torn down mid-navigation
            if (msg.includes('WKWebView was deallocated')) return null;

            // WebKit's generic fetch() network failure (Safari's "Network Error")
            if (firstValue?.type === 'TypeError' && msg === 'Load failed') return null;

            // Scripts injected into the HTML document itself (Chrome iOS page-translate):
            // every frame is the page URL, none is a .js asset. Kept narrow so a real
            // recursion inside /build/assets/*.js still reports.
            const onlyDocumentFrames = frames.length > 0 && frames.every((f) => f.filename && !/\.js(\?|$)/.test(f.filename));
            if (onlyDocumentFrames && (msg.includes('Maximum call stack size exceeded') || firstValue?.type === 'Error')) return null;

            // React reconciliation after an external DOM mutation (translate / extensions)
            if (firstValue?.type === 'NotFoundError' && msg.includes('The object can not be found here')) return null;

            // Inertia setPage() on an unparsable response body (flaky mobile network) — upstream gap
            if (msg.includes("evaluating 'e.toString'") && frames.some((f) => f.function === 'pageUrl')) return null;

            // Events with no useful data
            if (firstValue?.type === '<unknown>' && !msg) return null;

            return event;
        },
        // Session Replay
        replaysSessionSampleRate: import.meta.env.VITE_SENTRY_SESSIONS_SAMPLE_RATE,
        replaysOnErrorSampleRate: import.meta.env.VITE_SENTRY_SESSIONS_ERROR_RATE,
    });
}
